import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";

/**
 * RegenerateButton — bottone "Riprova" riusabile dopo qualsiasi operazione
 * automatica (foto enhance, restyle, staging, pianta). Chiama di nuovo lo
 * stesso endpoint con gli stessi parametri.
 *
 * iter v26 r5 — rinominato da "Rigenera foto" a "Riprova" (user feedback:
 * "rigenera"/"rifai" non sono chiari, "modifica" suggerisce un editing
 * sull'esistente. "Riprova" comunica meglio "il risultato non mi convince,
 * ripeti l'operazione").
 *
 * Soft-guard: dopo 2 tentativi chiede conferma esplicita con toast Sonner
 * (NIENTE window.confirm — bloccato silenziosamente da iOS PWA). Dopo 4
 * tentativi, blocca lato client (hard cap anti-abuso).
 *
 * Props:
 *   - onRegenerate: async () => void  · chiamata quando l'utente conferma
 *   - disabled: boolean               · es. durante un'altra operazione
 *   - kind: "photo" | "plan"          · per copy specifiche
 *   - testid: string                  · override testid
 */
const MAX_ATTEMPTS = 4;
const CONFIRM_THRESHOLD = 2;

export default function RegenerateButton({ onRegenerate, disabled, kind = "photo", testid = "regenerate-btn" }) {
  const [attempts, setAttempts] = useState(0);
  const [busy, setBusy] = useState(false);

  const label = "Riprova";
  const copyCost = "Ogni tentativo consuma 1 foto dal budget mensile.";

  const click = async () => {
    if (busy || disabled) return;
    if (attempts >= MAX_ATTEMPTS) {
      toast.error("Hai raggiunto il limite di 4 tentativi. Ricarica la pagina o cambia foto.");
      return;
    }
    // iter v26 r5 — sostituito window.confirm (bloccato da iOS PWA) con
    // toast Sonner: prima il bottone "Conferma riprova", poi (e solo poi)
    // parte l'operazione. Niente più silenzi sospetti per l'utente mobile.
    if (attempts >= CONFIRM_THRESHOLD) {
      const ok = await new Promise((resolve) => {
        let decided = false;
        const decide = (v) => { if (!decided) { decided = true; resolve(v); } };
        toast.warning(
          `Riprovare? ${copyCost} Se il risultato continua a non convincerti, prova a cambiare stile o ricarica una foto diversa.`,
          {
            duration: 10000,
            action: { label: "Conferma", onClick: () => decide(true) },
            cancel: { label: "Annulla", onClick: () => decide(false) },
            onDismiss: () => decide(false),
            onAutoClose: () => decide(false),
          },
        );
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await onRegenerate();
      setAttempts((a) => a + 1);
    } catch (e) {
      console.error("RegenerateButton: onRegenerate error", e);
      toast.error(e?.message || "Operazione non riuscita. Riprova fra un attimo.");
    } finally {
      setBusy(false);
    }
  };

  const remaining = MAX_ATTEMPTS - attempts;
  return (
    <button
      type="button"
      onClick={click}
      disabled={busy || disabled || attempts >= MAX_ATTEMPTS}
      className="inline-flex items-center gap-1.5 border border-[#EAE7DF] hover:border-[#7C8B6E] hover:bg-[#F1F4ED] text-[#1C1C1A] font-medium text-[12.5px] px-3.5 py-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testid}
      title={attempts > 0 ? `${attempts}/${MAX_ATTEMPTS} tentativi usati` : copyCost}
    >
      <ArrowsClockwise size={13} weight="bold" className={busy ? "animate-spin" : ""} />
      {busy ? "Riprovo…" : label}
      {attempts > 0 && attempts < MAX_ATTEMPTS && (
        <span className="text-[10px] text-[#9A968B] ml-0.5 tabular-nums">({remaining} rimasti)</span>
      )}
    </button>
  );
}
